import { Box, FormControl, FormLabel, Slide, Stack } from "@mui/material";
import {
  DataGridProProps, GridFilterModel, GridSortModel, GridToolbarColumnsButton, GridToolbarExport,
  GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import {
  Calendar, Leader, OrgUserAutocompleteOption, OrganizationLeader, RootState, SharedLeaderGrant, User
} from "../../../store";
import { useCallback, useEffect, useMemo, useState } from "react";

import BackupTable from "./BackupTable/BackupTable";
import colors from "../../../colors";
import { useLeaderColumns } from "./LeaderCols";
import UpgradeOverlay from "../../../components/Common/UpgradeOverlay/UpgradeOverlay";
import { CabButton } from "@CabComponents/CabButton";
import { EVENT_TYPE, NEW_TIER_IDS, USER_ROLE } from "../../../constants";
import AddLeader from "@CabComponents/CabNavBar/AddLeader";
import { CabModal } from "@CabComponents/CabModal";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { trackEvent } from "../../../utils/appAnalyticsUtils";
import AdminEditLeader from "./AdminEditLeader/AdminEditLeader";
import AdminReassignLeader from "./AdminReassignLeader/AdminReassignLeader";
import CabDataGrid from "@CabComponents/CabDataGrid";


export type SupportMapProps = {
  user: User | null | undefined
  leaders: OrganizationLeader[]
  sharedLeaderGrants: RootState["leaders"]["orgSharedLeaderGrants"]
  leaderCategories: RootState["leaders"]["orgLeaderCategories"]
  revokeGrant: (grant: SharedLeaderGrant) => void
  onCreateLeader: (
    leader: Leader, leaderPic: File | null, idx?: number, calendars?: number[]) => Promise<void>
  shareWithUserOptions: OrgUserAutocompleteOption[];
  onDeleteLeader: (leaderId: number) => void;
  loaded: boolean;
  hasEditLeaderPermission: boolean;
  calendars: Calendar[];
  onGetEmailCalendars: (email: string) => Promise<Calendar[]>;
};

const slots: DataGridProProps["slots"] = {
  noRowsOverlay: () => <Box
    sx={{
      height: "100%",
      width: "100%",
      display: "flex",
      justifyContent: "center"
    }}>
    <Box
      sx={{
        margin: "auto",
        padding: "auto"
      }}>
      No Teammates Available
    </Box>
  </Box>,
  noResultsOverlay: () => <Box
    sx={{
      height: "100%",
      width: "100%",
      display: "flex",
      justifyContent: "center"
    }}>
    <Box
      sx={{
        margin: "auto",
        padding: "auto"
      }}>
      No Teammates Found
    </Box>
  </Box>,
  toolbar: () => <Box
    sx={{
      display: "flex",
      flexDirection: "row-reverse",
      width: "100%"
    }}>
    <GridToolbarExport 
      printOptions={{ disableToolbarButton: true }}
      csvOptions={{
        fields: ['name', 'role', 'primary_assistant', 'backups'],
        fileName: `Cabinet_Support_Map_${new Date().toLocaleDateString('en')}_${new Date().toLocaleTimeString('en')}`,

      }}
    />
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
  </Box>
};

export default function SupportMap({ 
  user, leaders, sharedLeaderGrants, revokeGrant, leaderCategories, onCreateLeader, shareWithUserOptions,
  onDeleteLeader, loaded, hasEditLeaderPermission, calendars, onGetEmailCalendars
}: SupportMapProps) {
  const [backupModalLeader, setBackupModalLeader] = useState<OrganizationLeader | null>(null);
  const [editLeader, setEditLeader] = useState<OrganizationLeader | null>(null);
  const [reassignLeader, setReassignLeader] = useState<OrganizationLeader | null>(null);
  const [zIndexAdjustment, setZIndexAdjustment] = useState(false);
  const [showAddLeader, setShowAddLeader] = useState(false);
  const [deleteLeaderId, setDeleteLeaderId] = useState<number | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const onSelectLeaderBackupInfo = useCallback((leader: OrganizationLeader) => {
    return setBackupModalLeader(leader);
  }, []);

  const [filterState, setFilterState] = useState<GridFilterModel>();
  const [sortState, setSortState] = useState<GridSortModel>();
  const [paginationState, setPaginationState] = useState({ page: 0, pageSize: 20 });
  const hasStrategicSupportMap = [
    NEW_TIER_IDS.STRATEGIC,
    NEW_TIER_IDS.ENTERPRISE,
  ].includes(user?.active_license.license_tier || NEW_TIER_IDS.BASIC);
  const hasSupportMapAccess = hasStrategicSupportMap || !!user?.features?.SUPPORT_MAP;


  const onDeleteLeaderClick = () => {
    if (deleteLeaderId) {
      onDeleteLeader(deleteLeaderId);
      setDeleteLeaderId(null);
      setDeleteConfirmation('');
      trackEvent(EVENT_TYPE.DELETE_EXEC);
    }
  };

  const columns = useLeaderColumns({
    onSelectLeaderBackupInfo,
    onDeleteLeader: setDeleteLeaderId,
    hasEditLeaderPermission,
    onEditLeader: setEditLeader,
    onReassignLeader: setReassignLeader,
    canTransferLeader: !!user?.permissions.WRITE_LEADER_DATA_ACCESS,
    sharedLeaderGrants,
  });

  const dataRows = useMemo(() => (
    leaders || []
  ), [leaders]);

  // NOTE: This is setup to prevent the the overlay table from ever being behind the
  // initial table even during transitions
  useEffect(() => {
    if (backupModalLeader) {
      setZIndexAdjustment(!!backupModalLeader);
    } else {
      setTimeout(() => {
        setZIndexAdjustment(!!backupModalLeader);
      }, 500);
    }
  }, [backupModalLeader]);

  return (
    <Stack
      sx={{
        paddingBottom: 12,
        maxWidth: 900,
        gap: 2
      }}>
      
      <Box
        sx={{
          width: "100%",
          position: "relative",
          minHeight: 400
        }}>
        <Box
          sx={{
            zIndex: 1,
            position: "relative"
          }}>
          {hasSupportMapAccess && user?.permissions.WRITE_LEADER_DATA_ACCESS && (
            <CabButton
              onClick={() => setShowAddLeader(true)}
              sx={{width: 160}}
            >
              {user?.active_license.user_role === USER_ROLE.INDIVIDUAL ? 
                'Add Teammate' : 'Add Executive'
              }
            </CabButton>
          )}
          <CabDataGrid<OrganizationLeader>
            columns={columns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  backups: false, // Hide the "backups" column by default
                },
              },
            }}
            rows={dataRows}
            pagination
            pageSizeOptions={[20]}
            paginationModel={paginationState}
            onPaginationModelChange={setPaginationState}
            filterModel={filterState}
            onFilterModelChange={(data) => setFilterState(data)}
            sortModel={sortState}
            onSortModelChange={(data) => setSortState(data)}
            paginationMode="client"
            filterMode="client"
            sortingMode="client"
            showToolbar={!!slots?.toolbar}
            slots={slots}
            loading={!loaded}
            autoHeight
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: zIndexAdjustment ? 2 : 0
          }}>
          <Slide direction="left" in={!!backupModalLeader} mountOnEnter unmountOnExit>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                backgroundColor: colors.white900
              }}>
              <BackupTable
                user={user}
                leader={backupModalLeader || undefined}
                grants={backupModalLeader ? sharedLeaderGrants[backupModalLeader.id] : undefined}
                categories={backupModalLeader ? leaderCategories[backupModalLeader.id] : undefined}
                closeBackupTable={() => setBackupModalLeader(null)}
                revokeGrant={revokeGrant}
              />
            </Box>
          </Slide>
        </Box>
      </Box>
      {!hasSupportMapAccess && hasEditLeaderPermission && (
        <UpgradeOverlay
          show={true}
          overlaySx={{
            top: 105, height: '100%', position: 'absolute', width: '100%', zIndex: 100, 
            backgroundColor: 'rgba(0, 0, 0, .8)', left: 0, justifyContent: 'center', display: 'flex',
          }}
          message={'Upgrade your subscription plan to gain access to the Support Map'} />
      )
      }
      <AddLeader 
        open={showAddLeader} 
        onClose={() => setShowAddLeader(false)}
        onCreateLeader={onCreateLeader}
        calendars={calendars}
        onGetEmailCalendars={onGetEmailCalendars}
        shareWithUserOptions={shareWithUserOptions}
      />
      {!!editLeader && (
        <AdminEditLeader
          isOpen={!!editLeader}
          onDidDismiss={() => setEditLeader(null)}
          leader={editLeader}
        />
      )}
      {!!reassignLeader && (
        <AdminReassignLeader
          isOpen={!!reassignLeader}
          onDidDismiss={() => setReassignLeader(null)}
          leader={reassignLeader}
          shareWithUserOptions={shareWithUserOptions}
        />
      )}
      <CabModal
        open={!!deleteLeaderId}
        onClose={() => setDeleteLeaderId(null)}
        title={`Are you sure you want to delete this leader?`}
        text={
          "All meetings, profile information, and any other data associated with the leader "
          + "will be permanently deleted."
        }
        isAlert={true}
        noFullScreen={true}
        sx={{ ".MuiPaper-root": { width: "100%" } }}
        actionButtons={
          <>
            <CabButton buttonType='secondary' onClick={() => setDeleteLeaderId(null)}>
              Cancel
            </CabButton>
            <CabButton onClick={onDeleteLeaderClick}
              disabled={deleteConfirmation.toLowerCase() !== 'delete'}>
              Delete
            </CabButton>
          </>
        }
      >
        <FormControl fullWidth sx={{marginTop: 2}}>
          <FormLabel>Confirmation</FormLabel>
          <CabTextInput
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.currentTarget.value)}
            placeholder={'Type "DELETE" to confirm deletion'}
          />
        </FormControl>
      </CabModal>
    </Stack>
  );
}
